import { uniq } from 'lodash';

export const findAssignment = (assignments, letter) => {
  // possibly move this to the backend so assignments get returned grouped together
  const combinedLetterAssignments = assignments
    ?.filter(({ character }) => character.character.toLowerCase() === letter.toLowerCase())
    ?.reduce((acc, curr) => {
      acc['id'] = acc.id ?? curr?.id;
      acc['groups'] = [...(acc.groups || []), ...(curr.groups || [])];
      acc['students'] = [...(acc.students || []), ...(curr.students || [])];
      return acc;
    }, {});

  return combinedLetterAssignments;
};

export const getUpdatedGroups = (groupId: number, selectedGroups, allGroups, selectedStudents) => {
  const groups = [...selectedGroups];
  let updatedGroups = [] as number[];

  if (groups.includes(groupId)) {
    updatedGroups = groups.filter((g) => g !== groupId);
  } else {
    updatedGroups = [...groups, groupId];
  }

  // get students from group just selected
  // get students from other groups that do not have
  // the entire group selected
  const studentsFromGroups = updatedGroups
    .map((id) => {
      return getUpdatedStudentsFromGroup(id, allGroups);
    })
    ?.flat();
  const filteredSelectedStudents = removeStudentDuplicates(selectedStudents, allGroups);
  const updatedStudents = uniq([...filteredSelectedStudents, ...studentsFromGroups]);

  return { updatedGroups, updatedStudents };
};

export const getUpdatedStudentsFromGroup = (groupId, allGroups) => {
  const group = allGroups.find(({ id }) => id === groupId);
  if (!group) return;
  return getStudentsFromGroups([group]);
};

// gets all students from the groups provided
export const getStudentsFromGroups = (groups) => {
  if (!groups?.length) return groups;
  return groups
    .map(({ groupEnrollments }) => {
      if (!groupEnrollments?.length) return;
      return groupEnrollments.map((g) => {
        return g.student?.id || g.studentId;
      });
    })
    ?.flat();
};

export const getUpdatedStudents = (selectedStudents, studentId, allGroups) => {
  const copiedSelectedStudents = [...selectedStudents];
  let updatedStudents = [] as number[];

  if (copiedSelectedStudents.includes(studentId)) {
    updatedStudents = copiedSelectedStudents.filter((s) => s !== studentId);
  } else {
    updatedStudents = [...copiedSelectedStudents, studentId];
  }
  const updatedGroups = getGroupsWithAllStudentsSelected(updatedStudents, allGroups);

  return { updatedStudents, updatedGroups };
};

const areAllStudentsSelected = (groupEnrollments, students) => {
  return (
    groupEnrollments?.length &&
    groupEnrollments?.every((enrollment) => {
      return students.includes(enrollment.studentId);
    })
  );
};

// finds all groups that have all of the students selected
export const getGroupsWithAllStudentsSelected = (students, allGroups) => {
  return allGroups
    .map(({ id, groupEnrollments }) => {
      const allStudentsSelected = areAllStudentsSelected(groupEnrollments, students);
      if (allStudentsSelected) return id;
    })
    ?.filter((x) => x);
};

// removes students from student array if whole group is selected
export const removeStudentDuplicates = (selectedStudents, allGroups) => {
  const students = [...selectedStudents];
  const studentIds = [] as any[];
  allGroups.forEach(({ groupEnrollments }) => {
    const allStudentsSelected = areAllStudentsSelected(groupEnrollments, students);

    if (allStudentsSelected) {
      groupEnrollments.forEach((g) => {
        studentIds.push(g.studentId);
      });
    }
  });
  return students.filter((id) => !studentIds.includes(id));
};

export const getStudentsAndGroupsFromAssignments = (assignments, letter) => {
  const assignment = findAssignment(assignments, letter);
  const selectedGroups = assignment?.groups?.map((g) => g.id) ?? [];
  const selectedStudentsGroups = getStudentsFromGroups(assignment?.groups) ?? [];
  const selectedStudentsNoGroups = assignment?.students?.map((s) => s.id) ?? [];
  const selectedStudents = uniq([...selectedStudentsGroups, ...selectedStudentsNoGroups]);

  return { selectedGroups, selectedStudents };
};
